import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import type { CityDef } from '../../data/cities';
import { CITIES, getCity, hasServiceCityPage } from '../../data/cities';
import { NAV_SERVICES } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../CtaBand';
import FaqSection from '../FaqSection';
import Seo from '../Seo';

export default function CityPageTemplate({ city }: { city: CityDef }) {
  const path = locationPath(city.slug);
  const nearby = city.nearbySlugs.map((s) => getCity(s)).filter(Boolean) as CityDef[];

  const placeSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Epic Home Pros',
    description: city.metaDescription,
    url: `${SITE_URL}${path}`,
    telephone: PHONE_TEL,
    areaServed: {
      '@type': 'City',
      name: city.name,
      containedInPlace: { '@type': 'State', name: 'Texas' },
    },
  };

  const faqSchema = city.faqs.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: city.faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  return (
    <>
      <Seo
        title={city.metaTitle}
        description={city.metaDescription}
        path={path}
        jsonLd={faqSchema ? [placeSchema, faqSchema] : [placeSchema]}
      />

      <section className="bg-forest-tint py-16 sm:py-24">
        <div className="container-page">
          <div className="mx-auto max-w-3xl text-center">
            <div className="label-eyebrow">{city.regionLabel}</div>
            <h1 className="h-display mt-3">
              Home Remodeling in {city.name}, {city.state}
            </h1>
            <p className="mt-5 text-lg text-ink/75">{city.heroLead}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link className="btn-primary" to={ROUTES.QUOTE}>
                Get a Free Quote
              </Link>
              <a href={`tel:${PHONE_TEL}`} className="btn-secondary">
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl space-y-6">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{city.aboutTitle}</h2>
            {city.aboutParagraphs.map((p) => (
              <p key={p.slice(0, 48)} className="leading-relaxed text-ink/80">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Services in {city.name}</h2>
          <p className="mt-3 max-w-2xl text-ink/75">
            Kitchen, bath, ADU, and outdoor services across Texas - request a quote and we will follow up with next steps.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {NAV_SERVICES.map((service) => {
              const to = hasServiceCityPage(service.slug, city.slug)
                ? serviceCityPath(service.slug, city.slug)
                : servicePath(service.slug);
              return (
                <li key={service.slug}>
                  <Link
                    className="card-hover flex h-full flex-col rounded-xl border border-black/10 bg-white p-6 shadow-sm hover:border-forest/40"
                    to={to}
                  >
                    <h3 className="font-serif text-xl text-ink">{service.name}</h3>
                    <p className="mt-2 flex-1 text-sm text-ink/70">{service.heroLead}</p>
                    <span className="mt-4 text-sm font-semibold text-forest">
                      {hasServiceCityPage(service.slug, city.slug)
                        ? `View ${service.shortName} in ${city.name} →`
                        : `View ${service.name} →`}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{city.localNotesTitle}</h2>
            <ul className="mt-6 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {city.localNotes.map((note) => (
                <li key={note} className="leading-relaxed">
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {nearby.length > 0 && (
        <section className="bg-forest-tint py-16 sm:py-20">
          <div className="container-page">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Nearby cities</h2>
            <ul className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {nearby.map((n) => (
                <li key={n.slug}>
                  <Link
                    className="block rounded-lg border border-black/10 bg-white px-4 py-3 text-sm font-medium text-ink hover:border-forest/40 hover:text-forest"
                    to={locationPath(n.slug)}
                  >
                    {n.name}, {n.state}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm">
              <Link className="font-medium text-forest hover:underline" to={ROUTES.SERVICE_AREAS}>
                Browse by City
              </Link>
            </p>
          </div>
        </section>
      )}

      <FaqSection items={city.faqs} />
      <CtaBand
        title={`Ready for a quote in ${city.name}?`}
        lead="Free estimate. No obligation."
      />
    </>
  );
}

export function LocationsIndexHint() {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {CITIES.map((c) => (
        <li key={c.slug}>
          <Link
            className="card-hover block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
            to={locationPath(c.slug)}
          >
            <h3 className="font-serif text-xl text-ink">
              {c.name}, {c.state}
            </h3>
            <p className="mt-1 text-xs uppercase tracking-wider text-ink/50">{c.regionLabel}</p>
          </Link>
        </li>
      ))}
    </ul>
  );
}
